<template>
    <div class="row" style="width: 100%">
        <!-- asset name -->
        <div class="col-4">
            <div>{{ assetName }}</div>
        </div>
        <!-- is notified toggle -->
        <div class="col-4 q-pl-lg">
            <q-toggle
                v-model="accessIsNotified"
                color="accent"
                dense
                size="md"
            />
        </div>
        <!-- cause subscription dropdown -->
        <div class="col-4" style="min-width: 0; overflow: hidden">
            <FilterPanel
                :label="subscriptionCountLabel"
                @click="loadOptions"
                :loading="isLoading"
                :options="causesOptions"
                :selectedOptions="
                    new Set(subscribedCauses.map(cause => cause.cause))
                "
                @toggle="updateCauseSubscriptions"
                :searchOption="true"
                :outline="true"
                :alignLabel="'left'"
                :color="'black'"
                :dropdownMenuLabel="$t('Cause Alert Selection')"
                :icon-right="isMobile ? '' : 'fas fa-caret-down'"
                flat
                dense
            />
        </div>
    </div>
</template>
<script>
import FilterPanel from '../pages/Report/FilterPanel.vue';

export default {
    name: 'AssetAccess',
    emits: ['update:isNotified', 'update:causeSubscriptionCount'],
    components: {
        FilterPanel
    },
    props: {
        assetName: {
            type: String,
            required: true
        },
        assetId: {
            type: Number,
            required: true
        },
        assetAccessId: {
            type: Number,
            required: true
        },
        isNotified: {
            type: Boolean,
            required: true,
            default: false
        },
        causeSubscriptionCount: {
            type: Number,
            required: true,
            default: 0
        }
    },
    watch: {
        /**
         * Watch for changes in accessIsNotified and emit update event
         * @param newValue
         */
        accessIsNotified(newValue) {
            this.$emit('update:isNotified', newValue);
        },
        /**
         * Watch for changes in subscribedCauses and emit update event
         */
        subscribedCauses(newValue) {
            this.$emit('update:causeSubscriptionCount', newValue.length);
        }
    },
    mounted() {
        this.accessIsNotified = this.isNotified;
    },
    data() {
        return {
            accessIsNotified: false,
            showDropDown: false,
            causesOptions: [],
            subscribedCauses: [],
            isLoading: false
        };
    },
    computed: {
        /**
         * Label for cause subscription count
         * @return {string} - The subscription count label.
         */
        subscriptionCountLabel() {
            return this.causeSubscriptionCount + ' ' + this.$t('Subscribed');
        },
        /**
         * Determine if the device is mobile based on screen size
         * @return {boolean} - True if the device is mobile, false otherwise.
         */
        isMobile() {
            return this.$q.screen.lt.sm;
        }
    },
    methods: {
        /**
         * Load causes from backend API associated with the asset
         * @return {Array} - List of causes for the asset or empty array on error.
         */
        async loadAssetCauses() {
            let results = [];
            let nextUrl = `/assets/${this.assetId}/causes`;
            while (nextUrl) {
                try {
                    const response = await this.$axios.get(nextUrl);
                    results = results.concat(response.data.results);
                    nextUrl = response.data.next;
                } catch (error) {
                    console.error('Error loading asset causes:', error);
                    break;
                }
            }
            return results;
        },
        /**
         * Load cause subscriptions from backend API for the asset access
         * @return {Array} - List of cause subscriptions or empty array on error.
         */
        async loadCauseSubscriptions() {
            try {
                const response = await this.$axios.get(
                    `/cause_subscriptions/?asset_access_id=${this.assetAccessId}`
                );
                return response.data.results;
            } catch (error) {
                console.error('Error loading cause subscriptions:', error);
                return [];
            }
        },
        /**
         * Load options for the causes subscription filter panel
         */
        async loadOptions() {
            this.isLoading = true;
            try {
                // Emit event to parent to load causes for this asset access
                const causes = await this.loadAssetCauses();
                this.causesOptions = this.groupedCauseOptions(causes);
                this.subscribedCauses = await this.loadCauseSubscriptions();
            } finally {
                this.isLoading = false;
            }
        },
        /**
         * Group the causes by categories so that they can be
         * listed by tree used in filter-panel
         * @param causes causes grouped by Categories.
         */
        groupedCauseOptions(causes) {
            // If no causes, return empty array
            if (!causes || causes.length == 0) return [];

            const groupedCauses = causes.reduce((groupedOptions, cause) => {
                const hasCategory =
                    cause.category !== null && cause.category_name !== null;

                if (hasCategory) {
                    const categoryId = cause.category;
                    const categoryName = cause.category_name;
                    if (!groupedOptions[categoryId]) {
                        groupedOptions[categoryId] = {
                            label: categoryName,
                            value: categoryId + '_group',
                            selectable: false,
                            children: []
                        };
                    }

                    groupedOptions[categoryId].children.push({
                        label: cause.name,
                        value: cause.id,
                        is_subscribed: cause.is_subscribed
                    });
                } else {
                    groupedOptions[cause.id] = {
                        label: cause.name,
                        value: cause.id,
                        is_subscribed: cause.is_subscribed,
                        selectable: true,
                        children: []
                    };
                }

                return groupedOptions;
            }, {});

            // Convert object to array for FilterPanel
            return Object.values(groupedCauses);
        },
        /**
         * Update cause subscriptions based on user selection
         * @param selectedCauses causes selected by user
         */
        async updateCauseSubscriptions(selectedCauses) {
            // check which causes were added or removed
            const currentSubscribedCauses = this.subscribedCauses.map(
                cause => cause.cause
            );
            const diffSets = (a, b) => ({
                causesAdded: [...a].filter(x => !b.includes(x)),
                causesRemoved: b.filter(x => !a.has(x))
            });

            const { causesAdded, causesRemoved } = diffSets(
                selectedCauses,
                currentSubscribedCauses
            );
            if (causesAdded.length > 0) {
                try {
                    await this.$axios.post('/cause_subscriptions/bulk-add/', {
                        asset_access_id: this.assetAccessId,
                        cause_id_list: causesAdded
                    });
                } catch (error) {
                    console.error(`Error subscribing to event alerts:`, error);
                }
            }
            if (causesRemoved.length > 0) {
                try {
                    await this.$axios.post(
                        '/cause_subscriptions/bulk-remove/',
                        {
                            asset_access_id: this.assetAccessId,
                            cause_id_list: causesRemoved
                        }
                    );
                } catch (error) {
                    console.error(
                        `Error unsubscribing to event alerts:`,
                        error
                    );
                }
            }
            // Reload the subscribed causes after update
            if (causesAdded.length > 0 || causesRemoved.length > 0) {
                this.subscribedCauses = await this.loadCauseSubscriptions();
            }
        }
    }
};
</script>
<style scoped></style>
